import { useEffect } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CONTACT } from "../data/products";

const CHAPTERS = [
    {
        no: "01",
        title: "Who we are",
        text: "Mamta Enterprises is a wholesale supplier of drywall screws, fasteners and drywall accessories, based in West Bengal, India. We work with the people who build — contractors on site, retailers at the counter, and distributors moving stock across states.",
    },
    {
        no: "02",
        title: "What we stand for",
        text: "Consistent quality, honest packing counts and straight answers. Our four brands — Mamta, Tigerstar, Gripstar and Grey Patta — are packed to standard carton sizes so buyers always know exactly what they are getting.",
    },
    {
        no: "03",
        title: "How we work",
        text: "No carts, no checkout queues. You tell us the brand, size and quantity — by phone, email, WhatsApp or the enquiry form — and we come back with pricing and dispatch timelines. Wholesale, the way it should be.",
    },
];

const About = () => {
    useEffect(() => {
        document.title = "About Us — Mamta Enterprises";
    }, []);

    return (
        <main data-testid="about-page">
            {/* HEADER */}
            <section className="pt-32 sm:pt-40 pb-14 sm:pb-20 blueprint-grid spotlight border-b border-border" data-testid="about-header">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <MaskedLine delay={0.1}>
                        <span className="text-xs uppercase tracking-[0.3em] text-brand">About Us</span>
                    </MaskedLine>
                    <h1 className="font-display font-semibold uppercase tracking-tight text-5xl sm:text-7xl text-ink mt-3 leading-[0.95]">
                        <MaskedLine delay={0.25}>Built on</MaskedLine>
                        <MaskedLine delay={0.4}>
                            <span className="text-brand">trust.</span>
                        </MaskedLine>
                    </h1>
                </div>
            </section>

            {/* EDITORIAL SPLIT */}
            <section className="border-b border-border" data-testid="about-split-section">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2">
                    <div className="bg-ink blueprint-grid-dark flex items-center justify-center p-10 sm:p-16 min-h-[320px]">
                        <Reveal>
                            <div className="relative">
                                <div className="absolute -inset-4 border border-brand/40 translate-x-4 translate-y-4 pointer-events-none" />
                                <img
                                    src="/assets/logo.png"
                                    alt="Mamta Enterprises logo"
                                    className="w-52 h-52 sm:w-72 sm:h-72 object-cover bg-white rounded-full p-1 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
                                    data-testid="about-logo"
                                />
                            </div>
                        </Reveal>
                    </div>
                    <div className="bg-white p-10 sm:p-16 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-border">
                        <Reveal>
                            <span className="text-xs uppercase tracking-[0.3em] text-brand">West Bengal, India</span>
                            <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-5xl text-ink mt-3 leading-tight">
                                A trusted wholesale supplier — not just another trader.
                            </h2>
                            <p className="text-muted-foreground mt-6 text-base sm:text-lg leading-relaxed" data-testid="about-intro">
                                From our base in West Bengal, Mamta Enterprises has built its name one carton at a time. Buyers come back because the screws hold, the counts are right, and the phone is always answered.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-10 grid grid-cols-2 gap-px bg-border border border-border" data-testid="about-facts">
                                {[["4", "House brands"], ["Bulk", "Orders only"], ["WB", "Based in"], ["Pan-India", "Dispatch"]].map(([a, b]) => (
                                    <div key={b} className="bg-white p-5">
                                        <div className="font-display font-semibold text-2xl sm:text-3xl text-ink">{a}</div>
                                        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{b}</div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* MANIFESTO CHAPTERS */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="about-chapters">
                <Reveal>
                    <span className="text-xs uppercase tracking-[0.3em] text-brand">The Manifesto</span>
                    <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-5xl text-ink mt-2 mb-14">Three chapters, one promise</h2>
                </Reveal>
                <div className="space-y-px bg-border border border-border">
                    {CHAPTERS.map((ch, i) => (
                        <Reveal key={ch.no} delay={i * 0.1} className="bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-8 sm:p-12 items-start group hover:bg-brand-light/30 transition-colors" data-testid={`about-chapter-${ch.no}`}>
                                <div className="md:col-span-2">
                                    <span className="font-display font-semibold text-6xl sm:text-7xl text-outline-dark group-hover:text-brand group-hover:[-webkit-text-stroke:0px] transition-all">{ch.no}</span>
                                </div>
                                <h3 className="md:col-span-3 font-display font-medium uppercase tracking-wide text-2xl text-ink">{ch.title}</h3>
                                <p className="md:col-span-7 text-muted-foreground text-base leading-relaxed">{ch.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* SUPPLY CAPACITY */}
            <section className="bg-white border-y border-border" data-testid="about-capacity-section">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2">
                    <Reveal className="relative min-h-[320px] lg:min-h-[520px]">
                        <img
                            src="/assets/container.png"
                            alt="Grey Patta cartons loaded on pallets inside a shipping container"
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid="about-capacity-image"
                        />
                        <div className="absolute bottom-5 left-5 bg-brand text-white px-5 py-2.5 font-display uppercase tracking-[0.2em] text-xs" data-testid="about-capacity-badge">
                            Container-Load Dispatch
                        </div>
                    </Reveal>
                    <div className="p-10 sm:p-16 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-border">
                        <Reveal>
                            <span className="text-xs uppercase tracking-[0.3em] text-brand">Wholesale Capacity</span>
                            <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-5xl text-ink mt-3 leading-tight">
                                Palletised. Containerised. On its way.
                            </h2>
                            <p className="text-muted-foreground mt-6 text-base sm:text-lg leading-relaxed" data-testid="about-capacity-text">
                                From single cartons to full container loads, we pack, palletise and dispatch at scale. Our Grey Patta premium range moves by the container — proof of the supply capacity behind every order you place.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <ul className="mt-8 space-y-3" data-testid="about-capacity-points">
                                {["Full container and truckload dispatch", "Standardised cartons for easy stock counting", "Pan-India delivery for bulk buyers"].map((point) => (
                                    <li key={point} className="flex items-center gap-3 text-sm text-ink">
                                        <span className="w-2 h-2 bg-brand rotate-45 shrink-0" />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* MARQUEE + CTA */}
            <div className="bg-ink text-white py-5 border-y-4 border-brand" data-testid="about-marquee">
                <Marquee speed={30} gradient={false}>
                    {["TRUSTED BY CONTRACTORS", "RETAILERS", "DISTRIBUTORS", "ACROSS INDIA"].map((item) => (
                        <span key={item} className="flex items-center">
                            <span className="font-display uppercase tracking-[0.25em] text-lg sm:text-2xl px-8">{item}</span>
                            <span className="w-3 h-3 bg-brand inline-block rotate-45" />
                        </span>
                    ))}
                </Marquee>
            </div>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24 text-center" data-testid="about-cta">
                <Reveal>
                    <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-6xl text-ink leading-[1.02]">
                        Work with a supplier<br />who <span className="text-brand">answers.</span>
                    </h2>
                    <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
                        <Link
                            to="/contact"
                            data-testid="about-contact-button"
                            className="group inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-dark text-white font-display uppercase tracking-[0.15em] text-base px-10 py-4 transition-colors"
                        >
                            Contact Us <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </Link>
                        <a
                            href={`tel:+91${CONTACT.phone}`}
                            data-testid="about-call-button"
                            className="inline-flex items-center justify-center gap-3 border-2 border-ink text-ink hover:bg-ink hover:text-white font-display uppercase tracking-[0.15em] text-base px-10 py-4 transition-colors"
                        >
                            Call +91 {CONTACT.phone}
                        </a>
                    </div>
                </Reveal>
            </section>
        </main>
    );
};

export default About;
